/**
 * Orbi AI agent — shared constants for response routing, guardrail
 * classification, and request-context derivation (PCSA-13164). Kept framework-
 * agnostic so both the pure parser (shared/utils) and the NgRx slice can reuse
 * them, and so sibling stories (e.g. PCSA-13170 guardrail lock UX) consume the
 * same source of truth.
 */

/**
 * Agent response `returnType` discriminators the web client recognizes, aligned
 * to the values the agent service ACTUALLY emits. Verified against the agent
 * contracts (moonshot-ai-api):
 *  - Appointments — `AppointmentsAgentResponseModel.ReturnType`:
 *    `Text | AppointmentDetails | VisitHistory | AppointmentFindLab |
 *    AppointmentPatientPass | AppointmentCancelled | AppointmentSchedule`.
 *  - Billing — `BillingAgent.yaml` / `BillingAgentResponseModel.ReturnType`:
 *    `Text | PayABill | PaymentPlan | Invoices | InvoiceHistory |
 *    UpdateInsurance | FindABill`, with `data.invoices: string[]` (invoice
 *    NUMBERS only — the agent never returns invoice objects).
 *  - Results — sets NO returnType at all, so `PatientResults` is INFERRED
 *    client-side from `agent === 'Results'` + a list payload (mobile does the
 *    same inference; see `parseOrbiAgentResponse`).
 *  - Clinical / Support — text only (Support pins `Text`, Clinical emits none).
 *
 * Casing is not pinned by the gateway, so the `is*ReturnType` predicates match
 * case-insensitively (consistent with the rest of the parser).
 */
export const ORBI_RETURN_TYPE = {
  /**
   * Plain informational answer — no card and no navigation. Emitted explicitly
   * by the Appointments / Billing / Support agents (and it forcibly nulls
   * `data`); routed to the text path, same as an absent returnType.
   */
  Text: 'Text',
  /** Inferred client-side — the Results agent sets no returnType (see above). */
  PatientResults: 'PatientResults',
  VisitHistory: 'VisitHistory',
  AppointmentDetails: 'AppointmentDetails',
  AppointmentPatientPass: 'AppointmentPatientPass',
  AppointmentFindLab: 'AppointmentFindLab',
  /**
   * Successful appointment cancellation. `data` is a typed
   * `{ confirmationNumber, status: 'Cancelled' }` so the UI can refresh
   * deterministically instead of parsing the free-text response. Cancellation
   * FAILURES stay on the `Text` contract.
   */
  AppointmentCancelled: 'AppointmentCancelled',
  /** Appointments: no card; the text renders, then hands off to Find a Lab. */
  AppointmentSchedule: 'AppointmentSchedule',
  /** Billing: open Pay My Bill (checkout). Requires >= 1 invoice number. */
  PayABill: 'PayABill',
  /** Billing: open Create Payment Plan. Requires >= 1 invoice number. */
  PaymentPlan: 'PaymentPlan',
  /** Billing: navigate to Billing > Pay (invoice list). `data` optional. */
  Invoices: 'Invoices',
  /** Billing: navigate to Billing > View Payments. No `data`. */
  InvoiceHistory: 'InvoiceHistory',
  /** Billing: open Add/Update Insurance. Requires >= 1 invoice number. */
  UpdateInsurance: 'UpdateInsurance',
  /** Billing: show the Find a Bill card. `data.invoices` may be empty. */
  FindABill: 'FindABill',
  /**
   * Not emitted by any shipped agent — the favorite-locations agent never
   * landed (mobile's card is commented out as backlogged). Kept so the token is
   * documented in one place; it deliberately routes to text today.
   */
  FavoriteLocations: 'FavoriteLocations',
} as const;

export type OrbiReturnType =
  (typeof ORBI_RETURN_TYPE)[keyof typeof ORBI_RETURN_TYPE];

/**
 * Platform identifier the web client stamps on EVERY request to the agent
 * service (PCSA-13225). The agent service is shared across platforms; the
 * `web` value lets it distinguish a Patient Portal web request from the mobile
 * app and respond accordingly. Sent on messages, history, feedback, and any
 * other agent call — consistently, regardless of request type. Mobile sends its
 * own platform value on the equivalent header.
 */
export const ORBI_PLATFORM_HEADER = 'X-Platform';
export const ORBI_PLATFORM_WEB = 'web';

/**
 * Appointment-agent structured return types — everything the Appointments agent
 * emits EXCEPT its `Text` contract. `AppointmentDetails` / `VisitHistory` carry
 * an appointment COLLECTION (`data.appointments[]`, normalized server-side from
 * a bare array or the MCP `{content:[{text}]}` envelope); `AppointmentFindLab`,
 * `AppointmentPatientPass`, and `AppointmentCancelled` each carry a single typed
 * map, as does `AppointmentSchedule` (which renders no card). All six route to
 * `appointment-card`; the renderer branches on
 * `structuredData.returnType` (mobile renders VisitHistory in its own
 * visit-history card and the rest in appointment/patient-pass/find-lab cards).
 */
const ORBI_APPOINTMENT_RETURN_TYPES: readonly string[] = [
  ORBI_RETURN_TYPE.AppointmentDetails,
  ORBI_RETURN_TYPE.VisitHistory,
  ORBI_RETURN_TYPE.AppointmentPatientPass,
  ORBI_RETURN_TYPE.AppointmentFindLab,
  ORBI_RETURN_TYPE.AppointmentCancelled,
  ORBI_RETURN_TYPE.AppointmentSchedule,
];

/**
 * Billing-agent return types that signal a card / navigation action. The bare
 * `Text` value is excluded (informational answer, no `data`). Source of truth:
 * `BillingAgent.yaml` "# returnType values" — `PayABill`, `PaymentPlan`,
 * `Invoices`, `InvoiceHistory`, `UpdateInsurance`, `FindABill`.
 */
const ORBI_BILLING_RETURN_TYPES: readonly string[] = [
  ORBI_RETURN_TYPE.PayABill,
  ORBI_RETURN_TYPE.PaymentPlan,
  ORBI_RETURN_TYPE.Invoices,
  ORBI_RETURN_TYPE.InvoiceHistory,
  ORBI_RETURN_TYPE.UpdateInsurance,
  ORBI_RETURN_TYPE.FindABill,
];

/**
 * Membership test for a returnType token, ignoring case AND whitespace.
 *
 * Whitespace is stripped throughout, not just at the ends, because the agents do
 * emit spaced tokens: the QA Golden Dataset's Billing tab spells the same action
 * `PaymentPlan` on one row and `Payment Plan` on another. Comparing only on
 * trimmed lowercase let the spaced spelling fall through to the plain-text path,
 * so a payment-plan turn silently rendered as a bare message instead of
 * navigating. Every `ORBI_RETURN_TYPE` value is itself whitespace-free, so
 * removing whitespace can only widen matching to spellings the agents already
 * produce — it can never collide two distinct types.
 */
function matchesReturnType(
  returnType: string | undefined,
  candidates: readonly string[]
): boolean {
  if (!returnType) {
    return false;
  }
  const normalized = normalizeReturnType(returnType);
  return candidates.some(
    (candidate) => normalizeReturnType(candidate) === normalized
  );
}

/** Lowercase with all whitespace removed — see [matchesReturnType]. */
function normalizeReturnType(returnType: string): string {
  return returnType.replace(/\s+/g, '').toLowerCase();
}

/**
 * Case-insensitive equality for a single returnType token (PR 3163 review).
 *
 * Every OTHER comparison of a returnType in this codebase tolerates casing —
 * `isAppointmentReturnType` / `isBillingReturnType` above, and the parser's
 * `agent` check — because the gateway's casing is not pinned. A renderer that
 * fans out with `===` would therefore disagree with the router that sent the turn
 * to it: `visithistory` routes to `appointment-card` on the case-insensitive
 * predicate, then matches no `===` branch, and the card silently disappears
 * instead of rendering. Exported so the renderers use this one rule rather than
 * each spelling their own.
 */
export function isReturnType(
  returnType: string | undefined,
  expected: string
): boolean {
  return matchesReturnType(returnType, [expected]);
}

/** True for an appointment-agent structured return type (not its `Text`). */
export function isAppointmentReturnType(returnType?: string): boolean {
  return matchesReturnType(returnType, ORBI_APPOINTMENT_RETURN_TYPES);
}

/**
 * True for a billing-agent card/action return type (PCSA-13223) — see
 * [ORBI_BILLING_RETURN_TYPES]. Case-insensitive, since the gateway does not pin
 * returnType casing. `Text` is deliberately NOT a member: an informational
 * billing answer carries no `data` and must route to the plain text path.
 */
export function isBillingReturnType(returnType?: string): boolean {
  return matchesReturnType(returnType, ORBI_BILLING_RETURN_TYPES);
}

/**
 * Pause between a billing turn's ANSWER being on screen and the hand-off to the
 * portal page that answer names (PCSA-13416: "After the text response is
 * displayed, there is a brief delay (~1.5 seconds) before the web client
 * navigates the user to the destination page").
 *
 * The delay is the point, not an implementation detail: navigating the instant
 * the response paints would swap the page out from under the sentence the user
 * just started reading, and they would arrive at the destination without knowing
 * why. It is deliberately not long enough to feel like a stall.
 */
export const ORBI_BILLING_NAVIGATION_DELAY_MS = 1500;

/** AC 1.2's ~1.5 seconds, the same as the billing hand-off. */
export const ORBI_APPOINTMENT_SCHEDULE_NAVIGATION_DELAY_MS = 1500;

/**
 * Minutes BEFORE the appointment start at which mobile check-in opens
 * (PCSA-13397). Mirrors mobile `kMobileCheckInMinutesBefore` — the same 30-minute
 * window the app uses to switch the upcoming-appointment card into its check-in
 * presentation.
 */
export const ORBI_CHECK_IN_MINUTES_BEFORE = 30;

/**
 * Minutes AFTER the appointment start during which the pass / check-in surface
 * stays visible (PCSA-13397). Mirrors mobile
 * `kPostAppointmentPassVisibilityMinutes`. Past this grace period the card falls
 * back to its standard presentation.
 *
 * NOTE: this drives the card's TITLE only. Per AC 8 the frontend must NOT
 * calculate the 15-minute cutoff to decide whether Mobile Check-in is offered —
 * that is the agent's call (see [ORBI_APPOINTMENT_ACTION]).
 */
export const ORBI_POST_APPOINTMENT_GRACE_MINUTES = 15;

/**
 * Maximum appointment cards rendered from one `AppointmentDetails` turn.
 *
 * THREE, per the "Upcoming appointment multiple cap at 3" Figma frame, which
 * draws exactly three inner cards and then a "See All" CTA — the same treatment
 * (and the same number) as the Visit History card and mobile's
 * `UpcomingAppointmentsCarousel.maxItems`. AC 9 reads "the frontend SUPPORTS
 * displaying up to 5 appointment cards", which the renderer does; what it shows
 * by default is the design's call (AC 12 makes the design the authority), and
 * anything beyond the cap is reachable through See All rather than being listed.
 */
export const ORBI_MAX_APPOINTMENT_CARDS = 3;

/**
 * Actions an Upcoming Appointment card can offer (PCSA-13397 AC 5). Each maps to
 * an existing portal destination (AC 7):
 *  - `details`          → `/portal/appointments/detail/:confirmationCode`
 *  - `check-in`         → `/portal/appointments/check-in`
 *  - `provide-insurance`/`update-insurance` → `/portal/appointments/insurance/provider`
 *  - `review-insurance` → `/portal/appointments/insurance-review/:confirmationCode`
 *
 * AC 1.2/6 say the frontend must render only the actions the agent returns and
 * must not determine state itself. The shipped Appointments agent contract
 * (`AppointmentsAgentResponseModel` / `AppointmentsAgent.yaml`) carries NO
 * actions array — it passes the raw `getAppointments` row through unmodified — so
 * "returned by the agent response" is honored at the level the contract actually
 * supports: an action shows only when the payload it needs is present (see
 * `resolveOrbiAppointmentActions`), never from a client-side clock decision.
 * Mobile resolves the same way, and its chat card wires ONLY Appointment Details
 * (`orbi_chat_bubble.dart`: "Chat only owns Details").
 */
export const ORBI_APPOINTMENT_ACTION = {
  details: 'details',
  checkIn: 'check-in',
  provideInsurance: 'provide-insurance',
  reviewInsurance: 'review-insurance',
  updateInsurance: 'update-insurance',
} as const;

export type OrbiAppointmentAction =
  (typeof ORBI_APPOINTMENT_ACTION)[keyof typeof ORBI_APPOINTMENT_ACTION];

/**
 * Button labels for the appointment-card actions (PCSA-13397 AC 5), verbatim
 * from the AC and from the approved Figma frame, which draws the check-in
 * control as "Mobile Check-in" with a trailing phone glyph (mobile's
 * `_buildMobileCheckInButton`, Figma 17145:212325). The glyph is the template's
 * concern; this is the text only.
 */
export const ORBI_APPOINTMENT_ACTION_LABEL: Record<
  OrbiAppointmentAction,
  string
> = {
  [ORBI_APPOINTMENT_ACTION.details]: 'Appointment Details',
  [ORBI_APPOINTMENT_ACTION.checkIn]: 'Mobile Check-in',
  [ORBI_APPOINTMENT_ACTION.provideInsurance]: 'Provide Insurance',
  [ORBI_APPOINTMENT_ACTION.reviewInsurance]: 'Review Insurance',
  [ORBI_APPOINTMENT_ACTION.updateInsurance]: 'Update Insurance',
};

/**
 * What the check-in control reads once the patient has checked in (PCSA-13577).
 * It replaces the "Mobile Check-in" label rather than being a fourth action: the
 * Figma frame keeps the same filled pill in the same slot and only restates it,
 * which is also how mobile draws it (`_buildCheckedInDisabledButton`).
 */
export const ORBI_CHECKED_IN_LABEL = 'Checked In';

/**
 * The Upcoming Appointment card's "See All" CTA — the control the Figma draws
 * once at the foot of a capped set, in place of listing the remainder. Same copy
 * as [ORBI_VISIT_HISTORY_SEE_ALL_LABEL] and deliberately its own constant: the
 * two cards send the user to DIFFERENT screens (upcoming vs history), so a shared
 * label would tie two independent CTAs together.
 */
export const ORBI_APPOINTMENT_SEE_ALL_LABEL = 'See All';

/**
 * Google Static Maps parameters for the appointment card's map preview
 * (PCSA-13397 AC 3.4, "Map/location visual, when returned or available" — the
 * approved Figma draws one between the reason-for-visit line and the location).
 *
 * A static image is used rather than the portal's interactive `<google-map>`: the
 * card is a read-only summary inside the chat panel, and embedding a live map
 * instance per card would load the Maps JS SDK into the panel and put a pannable
 * surface inside a scrolling thread. The endpoint is already permitted by the
 * app's CSP (`img-src *.googleapis.com`) and keyed by the same
 * `EnvironmentService.googleMapsKey` the portal's geocoding already uses.
 */
export const ORBI_APPOINTMENT_MAP_PREVIEW = {
  /**
   * Requested width in CSS px. 640 is the Static Maps free-tier maximum before
   * `scale` is applied (mobile's `buildGoogleStaticMapUrl` clamps to the same
   * number), and it comfortably covers the panel at every breakpoint.
   */
  width: 640,
  /**
   * Rendered tile height, from mobile's `MapPreviewTile.height` default (134).
   * The requested image is asked for at this height too, so the aspect ratio
   * Google renders matches the box the browser paints it into.
   */
  height: 134,
  /** `scale: 2` for retina, as mobile does — 1280x268 of actual artwork. */
  scale: 2,
  /** Street level. Mobile's `buildGoogleStaticMapUrl` default. */
  zoom: 16,
  /**
   * Marker fill. `0x3866CD` is mobile's `LabcorpUIColors.link`, the color its
   * own static-map pin uses, so both platforms drop the same marker.
   *
   * Figma draws the lab as a gold STAR, which Static Maps can only do via a
   * custom `icon:` URL that Google's servers must fetch themselves — that cannot
   * resolve for a local or pre-release host, so the standard pin is used. Worth
   * confirming with design that the pin is acceptable on web.
   */
  markerColor: '0x3866CD',
} as const;

/**
 * Maximum past visits rendered inside one Visit History card. Mobile's
 * `CardCarousel.maxItems` default is 3 and its `SeeAllButton.previewCount` says
 * the same, and the Figma frames draw exactly three inner cards before the
 * "See All" CTA. Same number as [ORBI_MAX_APPOINTMENT_CARDS] — the two are
 * separate constants because they are separate design decisions, not because
 * they disagree.
 */
export const ORBI_MAX_VISIT_HISTORY_CARDS = 3;

/**
 * Copy for the Visit History card. The heading is "Visit History" at any count —
 * see `orbiVisitHistoryTitle`. Mobile pluralizes the noun
 * (`'${pluralize('Visit', n)} History'`) and web followed that rule until QA read
 * it against the Figma frames, which draw the same string over every state
 * (PCSA-13516).
 */
export const ORBI_VISIT_HISTORY_RATE_LABEL = 'Rate Visit';
export const ORBI_VISIT_HISTORY_SEE_ALL_LABEL = 'See All';

/**
 * Copy for the Patient Pass card (PCSA-13374 AC 2). Title and instructional
 * line are VERBATIM from mobile's compact `LabcorpPatientPassCard`, which is the
 * presentation the Orbi chat bubble uses (`compact: true`).
 */
export const ORBI_PATIENT_PASS_TITLE = 'MyLabcorp Pass';
export const ORBI_PATIENT_PASS_INSTRUCTIONS =
  'Scan the QR code at the Kiosk to check in.';
/**
 * The wallet controls render the platforms' official badge artwork, which carries
 * its own "Add to ... Wallet" wording as an image. These strings are therefore
 * the buttons' ACCESSIBLE NAMES rather than visible text, and each names its
 * specific wallet so screen-reader users get the same information sighted users
 * read off the badge — which matters most where both badges show and "Add to
 * wallet" twice would be indistinguishable.
 */
export const ORBI_PATIENT_PASS_APPLE_WALLET_LABEL = 'Add to Apple Wallet';
export const ORBI_PATIENT_PASS_GOOGLE_WALLET_LABEL = 'Add to Google Wallet';
/**
 * Shown on the card when a wallet download fails. Non-technical and retryable in
 * tone, matching the canvas's other inline failures (the copy control's), because
 * the badge stays enabled — the user can simply press it again.
 */
export const ORBI_PATIENT_PASS_WALLET_ERROR =
  'We could not add your pass right now. Please try again.';

/**
 * Copy for the Make an Appointment card (PCSA-13241 AC 2). Field labels, the
 * instructional line, the service-availability note, and the CTA are VERBATIM
 * from mobile's `LabSearchOptionCard.makeAppointment` so both platforms say the
 * same thing.
 */
export const ORBI_FIND_LAB_TITLE = 'Make an Appointment';
export const ORBI_FIND_LAB_INSTRUCTIONS =
  'To make an appointment, use the search below.';
export const ORBI_FIND_LAB_SERVICE_NOTE =
  'Please note: Not all lab locations offer all services. Appointments are ' +
  'recommended; walk-in availability varies.';
export const ORBI_FIND_LAB_LOCATION_LABEL = 'Enter ZIP or Address';
export const ORBI_FIND_LAB_SERVICE_LABEL = 'Reason for your visit';
export const ORBI_FIND_LAB_SUBMIT_LABEL = 'Find a Lab Location';

/** Empty-state text in the service dropdown (Figma: "Select Service"). */
export const ORBI_FIND_LAB_SERVICE_PLACEHOLDER = 'Select Service';

/**
 * Validation copy for the Make an Appointment card (PCSA-13241 AC 5: the user
 * must provide both criteria before searching). Both messages are VERBATIM from
 * the approved Figma error state, which words them as the ACTION the user still
 * owes ("Select a Service" / "Enter a ZIP code or Address") rather than as a
 * complaint. That differs from the portal modal's older wording; the Figma is the
 * newer approved design for this surface, so it wins here.
 */
export const ORBI_FIND_LAB_LOCATION_REQUIRED = 'Enter a ZIP code or Address';
export const ORBI_FIND_LAB_LOCATION_TOO_SHORT = 'Enter at least 3 characters';
export const ORBI_FIND_LAB_SERVICE_REQUIRED = 'Select a Service';

/**
 * Copy for the Find a Bill card (PCSA-13419). A `FindABill` turn means the
 * Billing agent could NOT match the bill to the signed-in account, so the card
 * asks for the three identifiers the invoice lookup needs (AC 3).
 *
 * There is NO mobile counterpart to mirror here: mobile's Orbi handles no
 * billing return type at all (its `FindABill` is an unrelated dashboard bite,
 * and `lib/orbi` only knows the appointment/results types), and its sole billing
 * touchpoint SSOs out to this portal. So the reference for this card is the
 * PORTAL's own registered-user bill lookup — `PortalDashboardInvoicesSearch` —
 * whose field labels, validation copy and BEHAVIOUR are reused rather than
 * reinvented, so a bill searched from the chat behaves exactly like the same bill
 * searched from the Billing page.
 *
 * Where the approved Figma for this card words something differently from that
 * page, the FIGMA wins (it is the newer approved design for this surface) and the
 * difference is called out at the constant. The three that differ are the
 * instructions, the alert heading and the contact line.
 */
export const ORBI_FIND_A_BILL_TITLE = 'Find a Bill';

/**
 * Figma names the three fields inline rather than saying "the information below",
 * so the user knows what to have to hand before reading down the form.
 */
export const ORBI_FIND_A_BILL_INSTRUCTIONS =
  'Enter the invoice number, zip code, and patient date of birth to search ' +
  'for your bill.';
export const ORBI_FIND_A_BILL_INVOICE_LABEL = 'Invoice Number';
export const ORBI_FIND_A_BILL_ZIP_LABEL = 'ZIP Code';
export const ORBI_FIND_A_BILL_DOB_LABEL = 'Date of Birth';

/** The card's two actions (AC 6). Labels match the portal's Billing page. */
export const ORBI_FIND_A_BILL_PAY_LABEL = 'Pay My Bill';
export const ORBI_FIND_A_BILL_INSURANCE_LABEL = 'Update Insurance';

/**
 * Lookup-failure copy (AC 9: stay in the card, explain, keep what was typed).
 * The structure and the two bulleted causes are the Billing page's own
 * `searchCriteria` alert — the failure IS the same failure, so it must not be
 * explained two different ways depending on where the search was started from.
 * Split into parts because the card draws them as a heading + list + note rather
 * than one paragraph.
 *
 * Two wordings follow the Figma instead of that page: the heading says "This
 * invoice" (the card asked about ONE invoice the user just typed, not their
 * account's bills), and the contact line names "Billing Customer Service" with
 * the BILLING number 800-845-6167 rather than "our Customer Service Department".
 */
export const ORBI_FIND_A_BILL_ERROR_TITLE =
  'This invoice is not available for online payment.';
export const ORBI_FIND_A_BILL_ERROR_INTRO = 'This could be because:';
export const ORBI_FIND_A_BILL_ERROR_REASONS: readonly string[] = [
  'You entered an incorrect invoice number and/or zip code.',
  'Labcorp has billed your insurance carrier, Medicare, or Medicaid and is ' +
    'waiting for a response.',
];
/**
 * The contact line is split around the PHONE NUMBER (PCSA-13312, 08/14 Vibe:
 * "make the billing customer service phone number clickable on mobile web"): the
 * card renders the number as a `tel:` link below md and as plain text above it,
 * which needs the number as its own node rather than buried in a sentence.
 *
 * `ORBI_FIND_A_BILL_ERROR_CONTACT` is recomposed from the three parts so the full
 * sentence stays ONE source of truth — the copy is asserted whole in specs, and a
 * second hand-written copy of it is how the link text and the plain text drift.
 */
export const ORBI_FIND_A_BILL_ERROR_CONTACT_BEFORE =
  'Contact Billing Customer Service at';
export const ORBI_FIND_A_BILL_ERROR_CONTACT_PHONE = '800-845-6167';
export const ORBI_FIND_A_BILL_ERROR_CONTACT_AFTER =
  'to verify the status of your invoice and determine financial responsibility.';
export const ORBI_FIND_A_BILL_ERROR_CONTACT =
  `${ORBI_FIND_A_BILL_ERROR_CONTACT_BEFORE} ` +
  `${ORBI_FIND_A_BILL_ERROR_CONTACT_PHONE} ` +
  ORBI_FIND_A_BILL_ERROR_CONTACT_AFTER;

/** Shown when the invoice is found but has nothing left to pay. */
export const ORBI_FIND_A_BILL_ZERO_BALANCE =
  'No payment due on this invoice at this time.';

/** Shown when the lookup itself failed (5xx / network), not the criteria. */
export const ORBI_FIND_A_BILL_TECH_ERROR =
  "We're experiencing technical difficulties. We're unable to complete your " +
  'request at this time. Please try again later.';

/**
 * Dismiss control on the error state. The Figma labels it "Go Back to Review",
 * which AC 9 does not name — the AC only requires the user to remain in the card
 * with their entries intact. That is exactly what this does: it clears the error
 * and returns to the filled-in form, so the Figma label is used as drawn.
 */
export const ORBI_FIND_A_BILL_ERROR_DISMISS_LABEL = 'Go Back to Review';

/**
 * Fallback copy shown when a text-routed agent turn has no content to display
 * (PCSA-13183 AC 5 / PCSA-13223 `fallback` route). Mirrors the mobile parser's
 * non-503 default (`orbi_chat_service.dart`). The transport/service-error
 * experience (503 retry pool, etc.) is PCSA-13172; this is only the empty /
 * no-renderable-content case surfaced in the thread.
 */
export const ORBI_FALLBACK_TEXT =
  'I encountered an error. Please try again later.';

/**
 * User-facing error copy for the agent error experience (PCSA-13172). Friendly,
 * non-technical, and free of raw status codes / backend language (AC 1/2). Each
 * maps to a supported failure scenario (AC 6) with a path forward (AC 3).
 *
 * NOTE: final copy is Content/Marketing-owned (per the ticket assumptions);
 * these are conservative placeholders aligned with the mobile wording
 * (`orbi_chat_service.dart` chatErrorMessages / defaults) where it overlaps.
 * Keyed by condition so `getOrbiUserFacingError` can map without exposing codes.
 */
export const ORBI_ERROR_TEXT = {
  /** 5xx / service unavailable / gateway timeout — temporary, try again later. */
  serverUnavailable:
    'The AI assistant is temporarily unavailable. Please try again later.',
  /** 403 — forbidden, without exposing auth/authz detail. */
  forbidden:
    "You don't have access to the AI assistant right now. Please try again later.",
  /** 404 — conversation/session not found; path forward = start new. */
  notFound:
    "I couldn't find that conversation. Try starting a new conversation.",
  /** 400 / other 4xx — request could not be processed; try again. */
  badRequest: "I couldn't process that request. Please try again.",
  /**
   * No network / request never completed (offline / CORS / timeout).
   *
   * "Please try again later", NOT "check your connection and try again" (design
   * review 08/28, Figma "UX FEEDBACK" -> Container). The bubble no longer carries
   * a retry control, so copy that asks the patient to act now would be pointing
   * at an affordance that is not there — and the web client cannot tell a dropped
   * connection from a service that is not answering, so telling someone to check
   * their own network is a guess that reads as blame.
   */
  connection: "I'm having trouble connecting. Please try again later.",
  /** Response failed while the agent was generating; retry supported. */
  generating: 'Something went wrong while I was responding. Please try again.',
  /** Unrecognized / fallback error. */
  unknown: 'Something went wrong. Please try again.',
} as const;

export type OrbiErrorTextKey = keyof typeof ORBI_ERROR_TEXT;

/**
 * Title the agent service FORCES onto a conversation whose guardrail trip locks
 * it (`AgentController`: set for every triggered guardrail type except OffTopic /
 * ForeignLanguage, overriding a user-edited title). It is the only
 * lock-discriminating signal that survives `GET /history`, because the service
 * strips the per-turn `returnType` metadata from that response
 * (`ChatMessageHelper.SanitizeForClient`) — so the read-only inference falls back
 * to this marker when a guardrail turn arrives without a type. Compared
 * case-insensitively after trimming; see `orbiHistoryIsReadOnly`.
 */
export const ORBI_GUARDRAIL_LOCK_TITLE = 'Blocked by Safety Guardrail';

/**
 * Guardrail types that are "soft" — surface a warning but do NOT lock the chat
 * (the user can retry on the next turn). Everything else that triggers
 * (EMERGENCY, HARM, SELFHARM, or any unknown type) is a hard lock. Values are
 * the NORMALIZED form (uppercase, separators stripped) — see
 * `normalizeOrbiGuardrailType` in shared/utils. Consumed by PCSA-13170's lock
 * experience as well as the PCSA-13164 classifier.
 */
export const ORBI_SOFT_GUARDRAIL_TYPES: readonly string[] = [
  'OFFTOPIC',
  'FOREIGNLANGUAGE',
];

/**
 * Approved page-context labels keyed by a route-URL fragment. Unlisted pages
 * send `[]`: a bare page name made the agent answer about the page instead of
 * the question.
 *
 * ORDER MATTERS: matching is a first-match substring test on the URL path, so a
 * more specific fragment must precede any fragment it contains. Fragments are
 * deliberately un-prefixed (`/results/detail`, not `/portal/results/detail`)
 * because the portal links resolve to both the `portal/`-prefixed and bare
 * forms depending on how the user entered the app.
 */
export const ORBI_REQUEST_CONTEXT_MAP: ReadonlyArray<{
  /** Lowercased URL fragment to match against the current router URL path. */
  urlFragment: string;
  /** Context label(s) sent to the agent for that page. */
  context: string[];
}> = [{ urlFragment: '/results/detail', context: ['Lab Results'] }];

/**
 * URL fragment identifying the results DETAIL page (`/portal/results/detail/
 * :reportId`). On this page the portal has already loaded the full accession
 * detail, so the request context includes that loaded data (PCSA-13222 AC 1/2)
 * — mirroring mobile's accession-details detail context
 * (`accession_details.dart` → `addDetailContext(json.encode(detail.toJson()))`).
 * Matched case-insensitively as a substring of the router URL path.
 */
export const ORBI_RESULTS_DETAIL_URL_FRAGMENT = '/results/detail';

/** Panel Trends page; its context is the panel name from the `testName` query param. */
export const ORBI_RESULTS_TREND_URL_FRAGMENT = '/results/trend';

/** A suggested starter prompt (PCSA-13159): a short chip label + the fully
 * formed question it pre-fills into the input. */
export interface OrbiSuggestedPrompt {
  /** Short chip label shown to the user. */
  shortPrompt: string;
  /** Fully formed question pre-filled into the input on selection. */
  fullPrompt: string;
}

/**
 * The APPROVED prompt list — the table in PCSA-13159 "Additional Information"
 * ("Suggested prompts from mobile app - all must be implemented on web"), which
 * is also the mobile app's `OrbiSuggestedTopics.allPrompts` (one-labcorp-mobile
 * `orbi_suggested_topics.dart`). All thirteen are here, label and pre-filled
 * question as the story writes them; the Figma mock only draws a few of them and
 * is not the source of this list.
 *
 * `Gut health` follows the STORY's casing rather than mobile's `Gut Health`: the
 * story table is the approved copy, and sentence case is what the other twelve
 * labels use.
 *
 * Hardcoded per the story's assumptions — not sourced from an API, not tailored
 * to the page (AC 5). A random subset (`ORBI_SUGGESTED_PROMPT_COUNT`) is shown
 * whenever the input is empty.
 */
export const ORBI_SUGGESTED_PROMPTS: readonly OrbiSuggestedPrompt[] = [
  {
    shortPrompt: 'Sleep and health',
    fullPrompt: 'How does sleep affect my health?',
  },
  { shortPrompt: 'Gut health', fullPrompt: 'How can I improve my gut health?' },
  {
    shortPrompt: 'Lifestyle changes',
    fullPrompt: 'What lifestyle changes impact my health the most?',
  },
  {
    shortPrompt: 'Annual checkups',
    fullPrompt: 'Why are annual checkups important?',
  },
  {
    shortPrompt: 'Normal blood pressure',
    fullPrompt: 'What is a normal blood pressure?',
  },
  {
    shortPrompt: 'Managing stress',
    fullPrompt: 'What are some healthy ways to manage stress?',
  },
  {
    shortPrompt: 'Lowering cancer risk',
    fullPrompt: 'What lifestyle changes can help lower my cancer risk?',
  },
  {
    shortPrompt: 'What is metabolic health?',
    fullPrompt: 'What is metabolic health?',
  },
  {
    shortPrompt: 'Latest results',
    fullPrompt: 'What are my latest test results?',
  },
  {
    shortPrompt: 'Prepare for labs',
    fullPrompt: 'How do I prepare for my upcoming labs?',
  },
  {
    shortPrompt: 'Learn about cholesterol',
    fullPrompt: 'What foods and lifestyle choices affect cholesterol?',
  },
  {
    shortPrompt: 'What is a lipid panel?',
    fullPrompt: 'Explain what a lipid panel tests',
  },
  {
    shortPrompt: 'What is a reference range?',
    fullPrompt: 'Explain reference range',
  },
];

/**
 * Max suggested prompts shown at once: THREE (PCSA-13312, design review 08/21 —
 * "Suggested prompts: reduce to three").
 *
 * This supersedes both PCSA-13159 AC 4 ("Display up to 6 prompts at once") and
 * mobile's own six (`OrbiSuggestedTopics._displayCount`), so web deliberately
 * shows fewer chips than the app does. The reason is the layout web ended up
 * with: the chips are a single VERTICAL stack at every breakpoint (QA 08/21
 * replaced AC 4.1's side-scrolling row), so each one costs the feed a full row
 * instead of sharing a line, and six of them pushed the newest response off
 * screen on a phone. Three is what the Figma canvas draws under a response.
 *
 * The approved list stays at thirteen (`ORBI_SUGGESTED_PROMPTS`) — this is how
 * many of them are on screen, not how many exist, so nothing is dropped from the
 * story's table and every prompt is still reachable across conversations.
 *
 * This counts the randomized prompts from `ORBI_SUGGESTED_PROMPTS` only. The
 * "How does this work?" chip is a DISTINCT, visually differentiated affordance
 * (AC 6) that is net-new for web and not part of the approved prompt list, so it
 * sits alongside the three rather than taking one of their places.
 */
export const ORBI_SUGGESTED_PROMPT_COUNT = 3;

/**
 * The distinct "How does this work?" prompt (PCSA-13159 AC 6) — net-new for web,
 * visually differentiated from the suggested prompts.
 *
 * Choosing it opens a real CONVERSATION TURN rather than an aside: the label is
 * echoed as the user's message and the explainer answers it as an ordinary agent
 * response (design review 08/11 — the "About Ask AI" frame draws exactly that).
 * The turn is composed client-side, so the label doubles as the user bubble's
 * text and nothing is sent to the agent.
 *
 * The explainer is MARKDOWN (it renders through the same `<markdown>` path as any
 * agent answer), hence the blank lines between paragraphs. Copy is the approved
 * Figma copy, and it deliberately disclaims medical advice, diagnosis and
 * treatment (AC 6c).
 */
export const ORBI_HOW_IT_WORKS_LABEL = 'How does this work?';
export const ORBI_HOW_IT_WORKS_EXPLAINER =
  "I'm your AI assistant.\n\n" +
  'I can help explain your lab results, answer questions about health topics, ' +
  'and connect you with relevant information.\n\n' +
  "I don't provide medical advice, diagnoses, or treatment recommendations.";

/**
 * `messageId` carried by the client-composed explainer turn.
 *
 * A FIXED id, not a generated one: it is what makes the turn addressable the way
 * every other response is (the feedback row keys its submitted-sentiment latch on
 * `messageId`), and it lets the reducer recognize the turn it already appended so
 * a second chip activation cannot duplicate the pair. Prefixed `local-` because
 * the gateway did not issue it — see the reducer for what that means for feedback.
 */
export const ORBI_HOW_IT_WORKS_MESSAGE_ID = 'local-how-it-works';

/**
 * `ToastService` scope for toasts the Orbi UI raises
 * (`toastService.show(text, { scope: ORBI_TOAST_SCOPE })`). They render only in
 * the Orbi panel's toast container, never in the app-level one.
 */
export const ORBI_TOAST_SCOPE = 'orbi';

/**
 * Heading and subtext for the "Make Sense of Your Results" static-prompt card
 * (PCSA static-prompts MVP). Shown on the Result Details screen above the
 * results, offering a small fixed set of starter questions that feed the Orbi
 * agent through the same `sendMessage` path the entry bar uses.
 *
 * System-generated, non-personalized content shown identically to every patient
 * — it carries NO PHI, patient identifier, or lab value.
 */
export const STATIC_RESULT_PROMPT_HEADING = 'Make Sense of Your Results';
export const STATIC_RESULT_PROMPT_SUBTEXT =
  'Use one of our suggested prompts to ask the AI assistant about your ' +
  'results...';

/**
 * The default static prompt set for the Result Details card. Reuses the
 * [OrbiSuggestedPrompt] shape so a prompt feeds the existing agent launch
 * unchanged (`sendMessage({ userMessage: fullPrompt, ... })`).
 *
 * A SEPARATE constant from [ORBI_SUGGESTED_PROMPTS] (the Orbi panel's rotating
 * general-health chips): these two are fixed, results-specific starters drawn
 * from the approved mockups, shown as a card on the results page rather than as
 * panel chips. Kept static and non-personalized (no PHI); the list supports
 * 1..N entries from this one default so later per-report-type variation is a
 * change to this constant, not a rewrite.
 */
export const STATIC_RESULT_PROMPTS: readonly OrbiSuggestedPrompt[] = [
  {
    shortPrompt: 'What does this result mean for me?',
    fullPrompt: 'What does this result mean for me?',
  },
  {
    shortPrompt: 'What does this test measure?',
    fullPrompt: 'What does this test measure?',
  },
];
